import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import WorkCard from "@/components/base/WorkCard";
import NewsCard from "@/components/base/NewsCard";
import PlotCard from "@/components/base/PlotCard";
import EmptyState from "@/components/base/EmptyState";
import PageHeader from "@/components/feature/PageHeader";
import { searchContent, getHotWorks } from "@/lib/content";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const TABS = [
  { key: "all", label: "全部" },
  { key: "works", label: "作品" },
  { key: "news", label: "资讯" },
  { key: "plots", label: "剧情" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState("all");

  useDocumentTitle(`${query ? `搜索：${query}` : "搜索"} - 黑皮辣妹`, "在黑皮辣妹影视频道检索影视资讯、剧情解析与影视作品。");

  useEffect(() => {
    setInput(query);
  }, [query]);

  const result = useMemo(() => searchContent(query), [query]);
  const totalCount = result.works.length + result.news.length + result.plots.length;
  const hotWorks = getHotWorks(4);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;
    setSearchParams({ q });
    setTab("all");
  };

  const showWorks = tab === "all" || tab === "works";
  const showNews = tab === "all" || tab === "news";
  const showPlots = tab === "all" || tab === "plots";

  return (
    <div className="hp-fade-up">
      <PageHeader
        title="搜索影视内容"
        description="输入关键词，快速检索影视资讯、剧情解析与影视作品，支持作品名、主创、类型与标签匹配。"
        breadcrumb={[{ label: "搜索" }]}
        eyebrow="Search"
      />

      <section className="w-full px-4 md:px-6">
        <form onSubmit={submit} className="mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex items-center flex-1 rounded-md border border-background-200 bg-background-50 overflow-hidden">
              <i className="ri-search-line pl-4 text-lg text-foreground-500" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="例如：暗涌 / 悬疑 / 陈牧 / 结局解析"
                className="flex-1 bg-transparent text-sm px-3 py-3.5 outline-none text-foreground-900 placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 text-sm font-medium px-7 py-3.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-search-line" />
              搜索
            </button>
          </div>
        </form>

        {query && (
          <div className="mb-6">
            <p className="text-sm text-foreground-500 mb-4">
              关键词 <span className="text-primary-600 font-medium">“{query}”</span> 共找到{" "}
              <span className="text-primary-600 font-medium">{totalCount}</span> 条相关内容
            </p>
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
              {TABS.map((t) => {
                const count =
                  t.key === "all"
                    ? totalCount
                    : t.key === "works"
                      ? result.works.length
                      : t.key === "news"
                        ? result.news.length
                        : result.plots.length;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`px-4 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                      tab === t.key
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {t.label} {count > 0 && <span className="font-label">({count})</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {(!query || totalCount === 0) && (
          <div className="space-y-10">
            <EmptyState
              icon="ri-search-eye-line"
              title={query ? "没有找到相关内容" : "开始搜索你感兴趣的影视内容"}
              description={
                query
                  ? "试试更换关键词，或浏览下方的热门作品推荐。"
                  : "输入作品名、导演、演员、类型或剧情关键词，即可检索资讯、解析与作品。"
              }
            />
            <div>
              <h2 className="font-heading font-black text-xl text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-fire-fill text-accent-500" />
                热门内容推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {hotWorks.map((w) => (
                  <WorkCard key={w.id} work={w} />
                ))}
              </div>
            </div>
          </div>
        )}

        {query && totalCount > 0 && (
          <div className="space-y-10">
            {showWorks && result.works.length > 0 && (
              <div>
                <h2 className="font-heading font-black text-xl text-foreground-950 mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-5 rounded-full bg-primary-500" />
                  影视作品（{result.works.length}）
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {result.works.map((w) => (
                    <WorkCard key={w.id} work={w} />
                  ))}
                </div>
              </div>
            )}

            {showNews && result.news.length > 0 && (
              <div>
                <h2 className="font-heading font-black text-xl text-foreground-950 mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-5 rounded-full bg-accent-500" />
                  影视资讯（{result.news.length}）
                </h2>
                <div className="space-y-3">
                  {result.news.map((n) => (
                    <NewsCard key={n.id} item={n} layout="row" />
                  ))}
                </div>
              </div>
            )}

            {showPlots && result.plots.length > 0 && (
              <div>
                <h2 className="font-heading font-black text-xl text-foreground-950 mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-5 rounded-full bg-secondary-500" />
                  剧情解析（{result.plots.length}）
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {result.plots.map((p) => (
                    <PlotCard key={p.id} item={p} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}